import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStore } from '../../../../store/store';
import { API_ENDPOINTS } from '../../../../api/endpoints';
import CompanyNameResultItem from './company-name/CompanyNameResultItem';
import ProfilerNameSearch from './shared/ProfilerNameSearch';
import Pagination from '../../../../components/navigation/Pagination';
import IconButton, { ICON_BUTTON_ICONS } from '../../../../components/buttons/IconButton';
import * as Portal from '@radix-ui/react-portal';
import { extractFirstUrl } from '../../../../utils/utils';
import { safeHttpUrl } from '../../../../components/profiler/CompanyDetails/KeyContacts/keyContactsUtils';

// Which row statuses offer the manual company search, and the one sentence that differs between
// them. Everything else about the block is shared, so the two states cannot drift apart visually.
// A status absent from this map gets no manual search at all (today: 'matched').
const MANUAL_SEARCH_LEAD_IN = {
  not_found:
    'No company matched this name. Search manually below, fix the spelling in your file, or leave it out.',
  review: 'Not the right company? Search manually below, or leave this name out.'
};

const TABS = [
  { key: 'review', label: 'Needs review' },
  { key: 'not_found', label: 'Not found' },
  { key: 'matched', label: 'Auto-matched' }
];

// The row badge names the backend status in the tab's own words, so badge and tab always read the
// same. It deliberately says nothing about why a row needs review (several candidates or one weak
// one): the candidates listed under the row already show that.
const STATUS_LABEL = Object.fromEntries(TABS.map((t) => [t.key, t.label]));

const PAGE_SIZE = 25;

const initSelections = (rows) => {
  const sel = {};
  rows.forEach((r) => {
    // Seed from the BE's own selection (candidate.isSelected) so the FE mirrors the DB state;
    // fall back to the top candidate for any row the worker left unselected.
    const preselected = r.candidates.filter((c) => c.isSelected).map((c) => c.id);
    const topCandidate = r.candidates.length ? [r.candidates[0].id] : [];
    sel[r.inputRow] = preselected.length ? preselected : topCandidate;
  });
  return sel;
};

const firstNonEmptyTab = (buckets) => (TABS.find((t) => buckets[t.key].length) || TABS[0]).key;

// Promise wrapper around the callback-based store.makeRequest so the finalize flow can await
// the selection sync then the finalize sequentially.
const requestOnce = (makeRequest, endpoint, body) =>
  new Promise((resolve, reject) => {
    makeRequest({
      endpoint,
      body,
      onSuccess: (data) => resolve(data),
      onError: (message, response) =>
        reject(new Error(message || response?.errorMessage || 'Request failed. Please try again.'))
    });
  });

// What "Open in new tab" hands to CompanyNameResultItem's onSuccess, shared by the candidate cards
// and the manual search. It carries the bulk searchId, as the bulk results view does, and no
// searchKey: with a searchKey (or with no context at all) the company page loads its header via
// get-profiler-company-from-search, which records a name search for every opened tab.
const newTabContext = (company, searchId) => ({
  companyId: company.id,
  ref: 'search results',
  searchId: searchId || undefined
});

const CandidateCard = ({ candidate, inputRow, searchId, selected, onToggle, utilsStore }) => (
  // A label + native (visually hidden) checkbox: clicks anywhere toggle, space toggles via
  // keyboard, and the selection state is conveyed by a real form control.
  <label className={`bulk-candidate-row${selected ? ' selected' : ''}`}>
    <input
      type="checkbox"
      className="bulk-check-input"
      checked={selected}
      onChange={onToggle}
      aria-label={`Select ${candidate.name}`}
    />
    <span className={`bulk-check${selected ? ' on' : ''}`} aria-hidden="true" />
    {/* Reuse the name-search result row for pixel-identical layout; selection handled by the
        wrapper. A plain click (no onSuccess) belongs to the label and must not navigate away from
        the review; only the context menu's "Open in new tab" passes onSuccess. The entry id is
        scoped to the row: the same company can be a candidate on several rows and in the open
        manual search, and the menu anchors to the first element carrying its id. */}
    <CompanyNameResultItem
      result={candidate}
      searchKey=""
      onNavigateToCompany={(company, onFinally, onSuccess) => {
        onSuccess?.(newTabContext(company, searchId));
        onFinally?.();
      }}
      utilsStore={utilsStore}
      inputId={`bulk-cand-${candidate.id}`}
      entryId={`bulk-candidate-entry-${inputRow}-${candidate.id}`}
    />
  </label>
);

CandidateCard.propTypes = {
  candidate: PropTypes.object.isRequired,
  inputRow: PropTypes.number.isRequired,
  searchId: PropTypes.string,
  selected: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  utilsStore: PropTypes.object.isRequired
};

// The uploaded website is user-typed, so it only becomes a link when it parses as an http(s) URL.
// A bare domain ("acme.de") gets https://; anything else (e.g. "javascript:…") stays plain text.
// The shown domain comes from the same parsed URL as the href, so the text always names the host
// the link opens: "https://www.acme.de/about" → "acme.de", "acme.de@evil.com" → "evil.com".
const parseWebsite = (website) => {
  const value = extractFirstUrl(website);
  const href = value && safeHttpUrl(value.includes('://') ? value : `https://${value}`);
  if (!href) return null;
  const url = new URL(href);
  return url.hostname.includes('.')
    ? { href: url.href, domain: url.hostname.replace(/^www\./, '') }
    : null;
};

/**
 * The optional template columns (city, country, website) the user uploaded for this row, shown next
 * to the uploaded name so the candidates can be compared against them. Renders only the parts that
 * were filled in, and nothing at all for a name-only row.
 */
const UploadedDetails = ({ row }) => {
  const location = [row.inputCity, row.inputCountry].filter(Boolean).join(', ');
  const website = row.inputWebsite;
  if (!location && !website) return null;

  const link = website ? parseWebsite(website) : null;

  return (
    <span className="bulk-uploaded-details">
      <span className="bulk-uploaded-list">
        {location && (
          <span className="bulk-uploaded-item" title={`Uploaded location: ${location}`}>
            <img src={ICON_BUTTON_ICONS.GEO_ALT} alt="" aria-hidden="true" />
            <span className="bulk-uploaded-text">{location}</span>
          </span>
        )}
        {website && (
          <span className="bulk-uploaded-item" title={`Uploaded website: ${website}`}>
            <img src={ICON_BUTTON_ICONS.GLOBE2} alt="" aria-hidden="true" />
            {link ? (
              <a
                className="bulk-uploaded-text bulk-uploaded-link"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Uploaded website ${link.domain} (opens in a new tab)`}
              >
                {link.domain}
              </a>
            ) : (
              <span className="bulk-uploaded-text">{website}</span>
            )}
          </span>
        )}
      </span>
    </span>
  );
};

UploadedDetails.propTypes = {
  row: PropTypes.shape({
    inputCity: PropTypes.string,
    inputCountry: PropTypes.string,
    inputWebsite: PropTypes.string
  }).isRequired
};

/**
 * The manual-resolution block for one uploaded row: a link that opens an inline company name
 * search seeded with the uploaded name, plus the in-progress and error feedback.
 *
 * Shared by "Not found" (TD-2365) and "Needs review" (TD-2743) rows — only `leadIn` differs,
 * which is the point: one component means the two states cannot drift apart visually.
 */
const ManualSearchBlock = ({
  row,
  leadIn,
  showLeadIn,
  isOpen,
  isAdding,
  error,
  onOpen,
  onClose,
  onPick,
  searchId,
  utilsStore
}) => (
  <div className="bulk-manual-resolve">
    {showLeadIn && <div className="bulk-manual-resolve-text">{leadIn}</div>}
    {isOpen ? (
      <div className="bulk-manual-search">
        <ProfilerNameSearch
          apiEndpoint={API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_NAME}
          ResultItemComponent={CompanyNameResultItem}
          resultItemProps={{
            // CompanyNameResultItem's contract is
            // (company, onFinally, onSuccess): a plain click passes
            // company only, while the context menu's "Open in new
            // tab" passes an onSuccess and expects the handler to
            // defer to it. Ignoring the third argument would make
            // "Open in new tab" silently resolve the row instead of
            // opening a tab.
            onNavigateToCompany: (company, onFinally, onSuccess) => {
              if (onSuccess) {
                onSuccess(newTabContext(company, searchId));
              } else {
                onPick(company);
              }
              onFinally?.();
            },
            utilsStore,
            inputId: `bulk-manual-search-${row.inputRow}`
          }}
          placeholder="Type company name here"
          label="Company name and legal form"
          inputId={`bulk-manual-search-${row.inputRow}`}
          initialSearchKey={row.inputName}
          disabled={isAdding}
        />
        <button type="button" className="bulk-link-btn" onClick={onClose}>
          Close search
        </button>
      </div>
    ) : (
      <button type="button" className="bulk-link-btn" onClick={onOpen}>
        Search manually
      </button>
    )}
    {isAdding && <div className="bulk-manual-resolve-text">Adding…</div>}
    {error && <div className="bulk-error">{error}</div>}
  </div>
);

ManualSearchBlock.propTypes = {
  row: PropTypes.object.isRequired,
  leadIn: PropTypes.string.isRequired,
  showLeadIn: PropTypes.bool,
  isOpen: PropTypes.bool,
  isAdding: PropTypes.bool,
  error: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  searchId: PropTypes.string,
  utilsStore: PropTypes.object.isRequired
};

/**
 * BulkUploadReview - the match-resolution / selection step of the bulk company search (TD-2344).
 *
 * Groups the uploaded names by outcome into tabs (Needs review / Not found / Auto-matched) so the
 * flow scales to hundreds of rows. Best match is pre-selected per name; the user can pick one OR
 * several (multi-select). "Continue" syncs the selection to the backend (only the rows that differ
 * from the worker's auto-selection) and finalizes the search, promoting the picks to the results.
 *
 * Rows the matcher could not settle offer a manual company search: not-found rows since TD-2365,
 * multi-match rows since TD-2743. Both render the same ManualSearchBlock.
 *
 * Rendered from three places — the Home bulk search (ProfilerSearch) and the results-page "Adjust
 * search" panel on desktop and mobile (BulkAdjustPanel) — so changes here land on all three.
 */
const BulkUploadReview = ({ results, searchId, onBack, onFinalized }) => {
  const { utilsStore, makeRequest } = useStore();
  const buckets = useMemo(
    () => ({
      review: results.rows.filter((r) => r.status === 'review'),
      not_found: results.rows.filter((r) => r.status === 'not_found'),
      matched: results.rows.filter((r) => r.status === 'matched')
    }),
    [results]
  );

  const [selections, setSelections] = useState(() => initSelections(results.rows));
  const [activeTab, setActiveTab] = useState(() => firstNonEmptyTab(buckets));
  const [page, setPage] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  // Manual resolution of not-found rows (TD-2365): companies the user picks via the embedded name
  // search, keyed by input row. Each is persisted as a real bulk_match_candidate (add endpoint) so
  // it can be selected + finalized exactly like an auto-match.
  const [manualCandidates, setManualCandidates] = useState({});
  // Only one picker is open at a time: ProfilerNameSearch calls cancelRequest(endpoint), which
  // aborts by endpoint path, so two open pickers would abort each other's in-flight search.
  const [searchOpenFor, setSearchOpenFor] = useState(null);
  const [addingFor, setAddingFor] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  // Rows whose selection the user has actively changed. A manual pick only replaces the matcher's
  // pre-selection on rows the user has left alone — see addManualCandidate. A ref, not state:
  // nothing renders from it, and it has to read *current* at the moment a pick is applied (after
  // the add's await), not as captured by the render that opened the picker.
  const touchedRows = useRef({});

  // The finalized set is addressed by searchId downstream (results view, save-as-project) and by
  // the manual-candidate endpoint. Prefer the prop; fall back to the id on the completed poll payload.
  const finalizedSearchId = searchId || results?.searchId;

  const toggle = (inputRow, id) => {
    // Clear any prior finalize error when the selection changes.
    setFinalizeError('');
    touchedRows.current[inputRow] = true;
    setSelections((prev) => {
      const current = prev[inputRow] || [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...prev, [inputRow]: next };
    });
  };

  const changeTab = (key) => {
    setActiveTab(key);
    setPage(0);
  };

  // Resolve a row manually: persist the user-picked company as a real (selected) candidate via the
  // add endpoint, then merge the returned candidate into local state so it renders + finalizes like
  // an auto-match. On failure the picker stays open with an inline error.
  const addManualCandidate = async (row, company) => {
    const { inputRow } = row;
    if (addingFor || !company?.id) {
      return;
    }
    if (!finalizedSearchId) {
      setRowErrors((prev) => ({
        ...prev,
        [inputRow]: 'Could not add this company (missing search id). Please re-upload your file.'
      }));
      return;
    }
    setFinalizeError('');
    setRowErrors((prev) => ({ ...prev, [inputRow]: '' }));

    // On a row that had suggestions, the user opened the search precisely because none of them
    // were right — so the pick replaces the matcher's pre-selection rather than joining it.
    // Unless they had already ticked something themselves, in which case both stay. Evaluated when
    // the pick is applied, not when it is made: a tick landed while the add was in flight still
    // counts, and a failed add — which applies nothing — leaves a retry free to replace.
    const replacesAutoSelection = () => row.candidates.length > 0 && !touchedRows.current[inputRow];

    // The picked company may already be one of this row's candidates — routine on a multi-match
    // row, impossible on a not-found one. Select the existing entry instead of adding a copy: the
    // backend upsert would merely merge is_selected, but the response would render as a second card.
    const existing = [...row.candidates, ...(manualCandidates[inputRow] || [])].find(
      (c) => c.id === company.id
    );
    if (existing) {
      // Read the touched state, then mark — a pick that lands makes the row touched, so a second
      // pick on the same row appends instead.
      const replaces = replacesAutoSelection();
      touchedRows.current[inputRow] = true;
      setSelections((prev) => {
        const current = prev[inputRow] || [];
        if (replaces) {
          return { ...prev, [inputRow]: [existing.id] };
        }
        return current.includes(existing.id)
          ? prev
          : { ...prev, [inputRow]: [...current, existing.id] };
      });
      setSearchOpenFor(null);
      return;
    }

    setAddingFor(inputRow);
    try {
      const data = await requestOnce(makeRequest, API_ENDPOINTS.ADD_BULK_COMPANY_CANDIDATE, {
        searchId: finalizedSearchId,
        inputRow,
        companyId: company.id
      });
      const candidate = data?.candidate;
      if (!candidate?.id) {
        throw new Error('Could not add this company. Please try again.');
      }
      setManualCandidates((prev) => {
        const current = prev[inputRow] || [];
        if (current.some((c) => c.id === candidate.id)) {
          return prev;
        }
        return { ...prev, [inputRow]: [...current, candidate] };
      });
      // The BE persists the pick as is_selected; mirror that in the FE selection. When the pick
      // replaces the pre-selection, the dropped candidate still carries isSelected:true from the
      // BE, so finalize()'s existing diff turns it into an UPDATE { selected: false }.
      const replaces = replacesAutoSelection();
      touchedRows.current[inputRow] = true;
      setSelections((prev) => {
        const current = prev[inputRow] || [];
        if (replaces) {
          return { ...prev, [inputRow]: [candidate.id] };
        }
        return current.includes(candidate.id)
          ? prev
          : { ...prev, [inputRow]: [...current, candidate.id] };
      });
      setSearchOpenFor(null);
    } catch (e) {
      setRowErrors((prev) => ({
        ...prev,
        [inputRow]: e.message || 'Could not add this company. Please try again.'
      }));
    } finally {
      setAddingFor(null);
    }
  };

  const totalSelected = Object.values(selections).reduce((n, arr) => n + arr.length, 0);
  const namesWithSelection = Object.values(selections).filter((arr) => arr.length).length;
  const { counts } = results;
  // Reflect manual resolution live: a not-found row with a selection now counts as matched, so the
  // summary doesn't keep showing it as "not found" after the user resolves it.
  const resolvedNotFound = buckets.not_found.filter(
    (r) => (selections[r.inputRow] || []).length > 0
  ).length;
  const matchedCount = counts.matched + resolvedNotFound;
  const notFoundCount = counts.notFound - resolvedNotFound;

  const activeRows = buckets[activeTab];
  const pages = Math.ceil(activeRows.length / PAGE_SIZE);
  const pageRows = activeRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const finalize = async () => {
    if (finalizing || totalSelected === 0) {
      return;
    }
    // Without a searchId there is nothing to open, so surface an error instead of navigating into
    // a blank view. (finalizedSearchId is resolved once in the component body.)
    if (!finalizedSearchId) {
      setFinalizeError(
        'Could not open your results (missing search id). Please re-upload your file.'
      );
      return;
    }
    setFinalizing(true);
    setFinalizeError('');
    try {
      // Push only the candidates whose selection differs from the worker's auto-selection.
      const updates = [];
      results.rows.forEach((row) => {
        const desired = new Set(selections[row.inputRow] || []);
        // Include manually-added candidates — otherwise a user-picked company for a not-found row
        // is never synced and silently drops at finalize.
        const rowCandidates = [...row.candidates, ...(manualCandidates[row.inputRow] || [])];
        rowCandidates.forEach((c) => {
          const want = desired.has(c.id);
          if (want !== Boolean(c.isSelected)) {
            updates.push({ inputRow: row.inputRow, companyId: c.id, selected: want });
          }
        });
      });
      for (const u of updates) {
        // Sequential: bounds BE load and surfaces the first failure cleanly.
        await requestOnce(makeRequest, API_ENDPOINTS.UPDATE_BULK_COMPANY_SELECTION, {
          searchId: finalizedSearchId,
          ...u
        });
      }
      await requestOnce(makeRequest, API_ENDPOINTS.FINALIZE_BULK_COMPANY_SEARCH, {
        searchId: finalizedSearchId
      });
      // Hand the finalized searchId back to the parent, which owns the router and opens the
      // standard results view (SEARCH_RESULTS?searchCategory=companyBulkSearch&searchId=…&page=1).
      onFinalized(finalizedSearchId);
    } catch (e) {
      setFinalizeError(e.message || 'Could not save your selection. Please try again.');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="bulk-review">
      <div className="bulk-review-summary">
        <button type="button" className="bulk-back" onClick={onBack}>
          &larr; Upload a different file
        </button>
        <div className="bulk-counts">
          <strong>{counts.rowsRead}</strong> names read
          {counts.duplicatesRemoved > 0 && (
            <> &middot; {counts.duplicatesRemoved} duplicates removed</>
          )}{' '}
          &middot; <strong>{matchedCount}</strong> matched &middot; <strong>{notFoundCount}</strong>{' '}
          not found
        </div>
      </div>

      <div className="bulk-tabs" role="tablist">
        {TABS.map((t) => {
          const count = buckets[t.key].length;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`bulk-tab${activeTab === t.key ? ' active' : ''}`}
              disabled={count === 0}
              onClick={() => changeTab(t.key)}
            >
              {t.label}
              <span className="bulk-tab-count">{count}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="bulk-tab-help-btn"
          aria-label="What do these tabs mean?"
          aria-expanded={showHelp}
          onClick={() => setShowHelp((v) => !v)}
        >
          <img src={ICON_BUTTON_ICONS.QUESTION_CIRCLE} alt="" />
        </button>
      </div>

      {showHelp && (
        <Portal.Root>
          <div className="how-to-profiler-search-popup-closer"></div>
          <div className="how-to-profiler-search-popup-wrapper">
            <div className="how-to-profiler-search-popup">
              <div className="header">
                <div className="title">What do these tabs mean?</div>
                <IconButton
                  id="close-bulk-tabs-help-popup"
                  withFill={false}
                  withBorder={false}
                  icon={ICON_BUTTON_ICONS.X_LG}
                  onClick={() => setShowHelp(false)}
                />
              </div>
              <div className="text-content">
                <div className="row">
                  <span className="highlight-how-to-profiler-search">Needs review</span>: names that
                  matched more than one company. Pick the right one, or several &mdash; or search
                  manually if none of them fit.
                </div>
                <div className="row">
                  <span className="highlight-how-to-profiler-search">Not found</span>: names with no
                  match. Search manually to pick the right company, or leave them out.
                </div>
                <div className="row">
                  <span className="highlight-how-to-profiler-search">Auto-matched</span>: names with
                  a single confident match, already selected. Change it if needed.
                </div>
              </div>
            </div>
          </div>
        </Portal.Root>
      )}

      <div className="bulk-tab-panel">
        {pageRows.map((row) => {
          const rowManual = manualCandidates[row.inputRow] || [];
          // Auto-match candidates plus any manually-picked companies for this row.
          const rowCandidates = [...row.candidates, ...rowManual];
          // A not-found row with a selection has been resolved manually — reflect that in the badge.
          // Review rows keep their badge: it names the status the matcher set, not what the user
          // did, and those rows already read as "has a selection".
          const resolvedManually =
            row.status === 'not_found' && (selections[row.inputRow] || []).length > 0;
          const manualLeadIn = MANUAL_SEARCH_LEAD_IN[row.status];
          return (
            <div className={`bulk-row status-${row.status}`} key={row.inputRow}>
              <div className="bulk-row-head">
                <div className="bulk-row-title">
                  <span className="bulk-input-name">&ldquo;{row.inputName}&rdquo;</span>
                  <UploadedDetails row={row} />
                </div>
                <span className={`bulk-status-badge ${resolvedManually ? 'matched' : row.status}`}>
                  {resolvedManually ? 'Resolved' : STATUS_LABEL[row.status]}
                </span>
              </div>
              {rowCandidates.length > 0 && (
                <div className="bulk-candidates profiler-name-search-results">
                  {rowCandidates.map((c) => (
                    <CandidateCard
                      key={c.id}
                      candidate={c}
                      inputRow={row.inputRow}
                      searchId={finalizedSearchId}
                      selected={(selections[row.inputRow] || []).includes(c.id)}
                      onToggle={() => toggle(row.inputRow, c.id)}
                      utilsStore={utilsStore}
                    />
                  ))}
                </div>
              )}
              {manualLeadIn && (
                <ManualSearchBlock
                  row={row}
                  leadIn={manualLeadIn}
                  showLeadIn={rowManual.length === 0}
                  isOpen={searchOpenFor === row.inputRow}
                  isAdding={addingFor === row.inputRow}
                  error={rowErrors[row.inputRow]}
                  onOpen={() => {
                    setRowErrors((prev) => ({ ...prev, [row.inputRow]: '' }));
                    setSearchOpenFor(row.inputRow);
                  }}
                  onClose={() => setSearchOpenFor(null)}
                  onPick={(company) => addManualCandidate(row, company)}
                  searchId={finalizedSearchId}
                  utilsStore={utilsStore}
                />
              )}
            </div>
          );
        })}
      </div>

      <Pagination
        show={pages > 1}
        page={page}
        pages={pages}
        onPageChange={(selected) => setPage(selected)}
      />

      <div className="bulk-review-footer">
        <div className="bulk-selected-count">
          <strong>{totalSelected}</strong> {totalSelected === 1 ? 'company' : 'companies'} selected{' '}
          across {namesWithSelection} {namesWithSelection === 1 ? 'name' : 'names'}
        </div>
        <div className="search-btn-wrap">
          <button
            type="button"
            className="btn btn-primary disable-fade-primary"
            disabled={totalSelected === 0 || finalizing}
            onClick={finalize}
          >
            {finalizing ? 'Saving…' : `Continue with ${totalSelected} selected`}
          </button>
        </div>
      </div>
      {finalizeError && <div className="bulk-confirm-note bulk-error">{finalizeError}</div>}
    </div>
  );
};

BulkUploadReview.propTypes = {
  results: PropTypes.object.isRequired,
  searchId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onFinalized: PropTypes.func.isRequired
};

export default BulkUploadReview;
